import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, computed, inject, input, LOCALE_ID, model, OnInit, signal, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, SystemInfoExtended, SystemInfoService, UrlService, } from "@tylertech/core-module";
import { ForgeCardModule, ForgeIconButtonModule, ForgeIconModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, ProfileSettingViewModel, SettingLookupOption, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfigurationCustomTypesPerDataset, ChartEvent, ChartType, Color, InteractionItem, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { SegmentService } from "../../services";

@Component({
    selector: "general-ledger-annual-expenditure-chart",
    templateUrl: "./annualExpenditureChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        TruncateTextComponent,
    ]
})
export class AnnualExpenditureChartComponent implements OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $intentService = inject(IntentService);
    private readonly $systemInfoService = inject(SystemInfoService);
    private readonly $segmentService = inject(SegmentService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $chartService = inject(ChartService);

    readonly chartId = input<string>(undefined, { transform: x => x as string ?? this.chartCriteria()?.ChartId });

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Monthly Expenditure";

    chartConfig: ChartConfigurationCustomTypesPerDataset;

    readonly chartCriteria = model<IChartCriteria | undefined>(undefined);

    private readonly isSchoolERPPro = signal(false);
    readonly isLoaded = signal<boolean>(false);

    readonly hasData = computed(() => this.chartCriteria()?.ChartDatas?.length > 0);
    readonly errorMessage = computed(() => this.chartCriteria()?.ErrorMessage);
    readonly subtitle = computed(() =>
    {
        const parameters = this.chartCriteria()?.Parameters;
        if (!parameters?.length)
        {
            return "";
        }

        let ret = "";
        const fiscalId1 = parameters?.find(x => x.Id === "ExpenditureFiscal1");
        if (fiscalId1?.Value)
        {
            const fiscalId1Desc = fiscalId1.Options.find(x => x.Value === fiscalId1.Value);
            if (fiscalId1Desc)
            {
                ret = ret + fiscalId1Desc.Display;
            }
        }

        const fiscalId2 = parameters?.find(x => x.Id === "ExpenditureFiscal2");
        if (fiscalId2?.Value)
        {
            const fiscalId2Desc = fiscalId2.Options.find(x => x.Value === fiscalId2.Value);
            if (fiscalId2Desc)
            {
                ret = ret + " & " + fiscalId2Desc.Display;
            }
        }

        const funds = parameters?.find(v => v.Id === "ExpenditureFundIds");
        if (funds?.Value)
        {
            ret = ret + " &bull; By Funds";
        }

        const groupBy = parameters?.find(v => v.Id === "ExpenditureGroupById");
        if (groupBy?.Value)
        {
            const groupByDesc = groupBy.Options.find(x => x.Value === groupBy.Value);
            if (groupByDesc)
            {
                ret = ret + " &bull; By " + groupBy.Options.filter(x => x.Value === groupBy.Value)[0].Display;
            }
        }

        return ret;
    });

    private chartDataColors: ReadonlyArray<Color>;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);
        $chartManagerService.refreshChartObservable
            .pipe(takeUntilDestroyed())
            .subscribe((chartId: string) =>
            {
                if (chartId !== this.chartId())
                {
                    return;
                }
                this.loadChartData();
            });

        this.chartDataColors = this.$chartService.getColors(5);

        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    ngOnInit(): void
    {
        this.loadLicenses()
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return this.loadChartData();
                    }

                    this.updateChartConfiguration();
                }
            )
            .finally(() =>
            {
                this.isLoaded.set(true);
            });
    }

    private getParameterById(parameterId: string): ProfileSettingViewModel | undefined
    {
        const chartCriteria = this.chartCriteria();
        const parameters = chartCriteria?.Parameters?.find(g => g.Id === parameterId);
        return parameters;
    }

    private getParameterField<T>(parameterId: string, fieldName: string): T
    {
        const parameter = this.getParameterById(parameterId);
        return parameter ? parameter[fieldName] : null;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded.set(false);
        return this.$chartRegistryService.GetChartData(this.chartCriteria())
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria.set(result);
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria.set({
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria);
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error("InventoryTopUsedItemsChartComponent | error", err);
                }
            )
            .finally(() =>
            {
                this.isLoaded.set(true);
            });
    }

    private loadLicenses(): Promise<void>
    {
        return this.$systemInfoService.GetCachedSystemInfo().then((result: SystemInfoExtended) =>
        {
            this.isSchoolERPPro.set(result.IsSchoolERPPro());
        });
    }

    openSettings(): void
    {
        this.$chartDialogService
            .showChartParametersDialog(this.ChartTitle, this.chartCriteria())
            .then((criteria: IChartCriteria) =>
            {
                const newValue = this.getParameterField<string>("ExpenditureGroupById", "Value");
                if (newValue)
                {
                    this.$segmentService.GetSubSegmentValues(newValue).then(result =>
                    {
                        const sub = this.getParameterById("ExpenditureGroupBySubId");
                        if (sub)
                        {
                            sub.Options = [
                                <SettingLookupOption>{
                                    Value: "",
                                    Display: "View All",
                                },
                                ...result.map(
                                    x =>
                                        <SettingLookupOption>{
                                            Value: x.Id,
                                            Display: x.Name,
                                        }
                                ),
                            ];
                            sub.Value = "";
                        }
                    });
                }
                this.chartCriteria.set(criteria);
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const trendLineColor = this.$chartService.getTrendLineColor();
        const chartCriteria = this.chartCriteria();
        const numDatasets = chartCriteria.ChartDatas.length;
        this.chartConfig = {
            options: {
                maintainAspectRatio: false,
                responsive: true,
                scales: {
                    x: {
                        type: "category",
                        display: true,
                        grid: { display: true },
                        labels: chartCriteria.ChartDatas[0].Labels,
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        title: {
                            display: true,
                            text: this.isSchoolERPPro()
                                ? numDatasets === 2
                                    ? chartCriteria.ChartDatas[0].SetLabel
                                    : `${chartCriteria.ChartDatas[0].SetLabel.substring(0, 8)} / ${chartCriteria.ChartDatas[1].SetLabel.substring(0, 8)}`
                                : numDatasets === 2
                                    ? chartCriteria.ChartDatas[0].SetLabel
                                    : `${chartCriteria.ChartDatas[0].SetLabel} / ${chartCriteria.ChartDatas[1].SetLabel}`,
                            font: { size: 18, },
                        },
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    lineAxis: {
                        type: "linear",
                        display: true,
                        position: "left",
                        title: {
                            display: true,
                            text: numDatasets === 2
                                ? chartCriteria.ChartDatas[1].SetLabel
                                : chartCriteria.ChartDatas[2].SetLabel,
                            font: { size: 16, },
                        },
                        grid: { display: false },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const chart = legend.chart;
                            const meta = chart.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !chart.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (chart.data.datasets || []).length; ++i)
                            {
                                const iMeta = chart.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            chart.options.scales[yAxisID].display = showScale;
                            chart.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: Array<TooltipItem<ChartType>>): string | Array<string> =>
                            {
                                return tooltipItems[0].label;
                            },
                            label: (tooltipItem: TooltipItem<ChartType>): string | Array<string> =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        }
                    },
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,
                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return;
                    }

                    this.drillIn(element);
                },
            },
            data: {
                datasets: chartCriteria.ChartDatas.length === 3
                    ? [
                        {
                            type: "bar",
                            label: `${chartCriteria.ChartDatas[0].SetLabel}`,
                            data: chartCriteria.ChartDatas[0].Data,
                            backgroundColor: this.chartDataColors[0],
                            borderColor: this.chartDataColors[0],
                            hoverBackgroundColor: this.chartDataColors[0],
                            hoverBorderColor: this.chartDataColors[0],
                            yAxisID: "barAxis",
                        },
                        {
                            type: "bar",
                            label: `${chartCriteria.ChartDatas[1].SetLabel}`,
                            data: chartCriteria.ChartDatas[1].Data,
                            backgroundColor: this.chartDataColors[1],
                            borderColor: this.chartDataColors[1],
                            hoverBackgroundColor: this.chartDataColors[1],
                            hoverBorderColor: this.chartDataColors[1],
                            yAxisID: "barAxis",
                        },
                        {
                            type: "line",
                            label: `${chartCriteria.ChartDatas[2].SetLabel}`,
                            data: chartCriteria.ChartDatas[2].Data,
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                            yAxisID: "lineAxis",
                        },
                    ]
                    : [
                        {
                            type: "bar",
                            label: `${chartCriteria.ChartDatas[0].SetLabel}`,
                            data: chartCriteria.ChartDatas[0].Data,
                            backgroundColor: this.chartDataColors[3],
                            borderColor: this.chartDataColors[3],
                            hoverBackgroundColor: this.chartDataColors[3],
                            hoverBorderColor: this.chartDataColors[3],
                            yAxisID: "barAxis",
                        },
                        {
                            type: "line",
                            label: `${chartCriteria.ChartDatas[1].SetLabel}`,
                            data: chartCriteria.ChartDatas[1].Data,
                            borderColor: trendLineColor,
                            backgroundColor: trendLineColor,
                            pointHoverBackgroundColor: trendLineColor,
                            pointHoverBorderColor: trendLineColor,
                            yAxisID: "lineAxis",
                        },
                    ],
            },
        };
    }

    private drillIn(element: InteractionItem): void
    {
        const index = element.index;
        const datasetIndex = element.datasetIndex;
        const chartCriteria = this.chartCriteria();
        const subset: ProfileSettingViewModel = chartCriteria?.Parameters
            ?.find(v => v.Id === "ExpenditureGroupBySubId");
        const expenditureGroupById = chartCriteria?.Parameters
            ?.find(v => v.Id === "ExpenditureGroupById");
        const groupBy = expenditureGroupById?.Options
            .find(x => x.Value === expenditureGroupById.Value)
            ?.Display;

        let fiscalId;
        let periodId;
        if (datasetIndex === 0)
        {
            fiscalId = chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal2")?.Value;
            periodId = index + 1;
        }
        else if (datasetIndex === 1)
        {
            fiscalId = chartCriteria?.Parameters?.find(x => x.Id === "ExpenditureFiscal1")?.Value;
            periodId = index + 1;
        }
        else
        {
            return;
        }

        const filter: Array<FilterItem> = [
            {
                Key: "BudgetCodeFiscalId",
                Value: fiscalId,
            },
            {
                Key: "PeriodId",
                Value: periodId,
            },
            {
                Key: "AccountType",
                Value: "Expense",
            },
            {
                Key: "Account Status",
                Value: "Active",
            },
            {
                Key: "NonZeroActivity",
                Value: "true",
            },
        ];

        const expenditureFundIds = chartCriteria?.Parameters
            ?.find(x => x.Id === "ExpenditureFundIds")?.Value;

        if (expenditureFundIds)
        {
            filter.push({
                Key: "FundIds",
                Value: expenditureFundIds,
            });
        }

        if (chartCriteria.ChartDatas[0].Additional && chartCriteria.ChartDatas[0].Additional[0])
        {
            filter.push(chartCriteria.ChartDatas[0].Additional[0]);
        }

        if (subset.Value)
        {
            const subs = subset.Value.split(",").filter((x: string) => !Utilities.IsNullOrUndefinedOrEmpty(x));
            const queryString = subs.reduce(
                (c, n) => c + ` "${groupBy}"="${subset.Options.find(v => v.Value === n)?.Display}"`,
                "");

            filter.push({
                Key: "QueryString",
                Value: queryString,
            });
        }

        const parms: AppLinkParms = {
            App: "glaccounts",
            Parms: filter,
        };

        this.$urlService.GetAppLink(parms)
            .then((executionResult: ExecutionResult<string>): void =>
            {
                const index = executionResult.Result.indexOf("?");
                if (executionResult.Result.includes("apps/form"))
                {
                    this.$navigationService.navigateToApp(executionResult.Result);
                }
                else if (index > 0)
                {
                    this.$intentService.resolve("GLAccounts", "Search", {
                        querystring: executionResult.Result.substring(index, executionResult.Result.length),
                    });
                }
            });
    }
}
